import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { chatApi } from '../../api/chat.api'
import { usersApi } from '../../api/users.api'
import { ApiError } from '../../api/client'
import { toast } from '../../store/toastStore'
import formStyles from '../../components/ui/Form.module.css'

export function NewChannelModal({ onClose, onCreated }: { onClose: () => void; onCreated: (channelId: string) => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [memberUserIds, setMemberUserIds] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const usersQuery = useQuery({ queryKey: ['users', { pageSize: 100 }], queryFn: async () => (await usersApi.list({ pageSize: 100 })).data })

  const mutation = useMutation({
    mutationFn: () => chatApi.createChannel({ name, isPrivate, memberUserIds }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['chat', 'channels'] })
      toast.success('Channel angelegt.')
      onCreated(res.data.id)
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Anlegen fehlgeschlagen.'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate()
  }

  return (
    <Modal
      title="Neuer Channel"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={mutation.isPending}>
            Anlegen
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}
        <Field label="Name" htmlFor="ch-name">
          <Input id="ch-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)' }}>
          <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
          Privat (nur eingeladene Mitglieder)
        </label>
        <Field label="Mitglieder einladen (optional)" hint="Du bist automatisch Mitglied. Kann später über 'Mitglieder' im Channel geändert werden.">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {usersQuery.data?.map((user) => {
              const checked = memberUserIds.includes(user.id)
              return (
                <label
                  key={user.id}
                  className={formStyles.checkboxRow}
                  style={{ border: '1px solid var(--surface-border)', borderRadius: 'var(--radius-md)', padding: '4px 10px' }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setMemberUserIds((prev) => (checked ? prev.filter((id) => id !== user.id) : [...prev, user.id]))}
                  />
                  {user.displayName}
                </label>
              )
            })}
          </div>
        </Field>
      </form>
    </Modal>
  )
}
